import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import styled from 'styled-components';

import { Col, Row, Checkbox, Button } from 'antd';

import { PitstopModal } from 'shared';
import UsersTable from '../UsersTable';
import TableStore from 'stores/abstract/TableStore';
import { AppStore, ShopStore } from 'stores';
import { observer } from 'mobx-react';
import SuccessModal from './SuccessModal';
import PropTypes from 'prop-types';

const StyledModal = styled(PitstopModal)`
  && {
    .ant-modal-header {
      margin-bottom: 30px;

      .ant-modal-title {
        color: #02103d;
      }
    }

    .ant-table {
      max-width: 800px !important;
      table-layout: fixed !important;
    }
  }
`;

const initialState = {
  selectedNotificationTypes: {
    byEmail: false,
    bySMS: false,
  },
  selectedUsers: [],
  saving: false,
};

const WorkOrderModal = (props) => {
  const { title, modalId } = props;

  const [savedState, setSavedState] = useState(initialState);
  const [state, setState] = useState(initialState);

  const EDIT_WORK_ORDER_NOTIFICATION_MODAL_ID =
    'EDIT_WORK_ORDER_NOTIFICATION_MODAL_ID';
  const EDIT_WORK_ORDER_NOTIFICATION_SUCCESS_MODAL_ID =
    'EDIT_WORK_ORDER_NOTIFICATION_SUCCESS_MODAL_ID';

  const userTableRef = useRef();
  const [tableStore] = useState(() => new TableStore());

  const onChangeUsersSelected = (selectedUsers) => {
    setState((prevState) => ({ ...prevState, selectedUsers }));
  };

  const onChange = (notificationTypeKey, checked) => {
    setState((prevState) => {
      const selectedNotificationTypes = {
        ...prevState.selectedNotificationTypes,
      };
      _.set(selectedNotificationTypes, notificationTypeKey, checked);
      return { ...prevState, selectedNotificationTypes };
    });
  };

  const computeSelections = () => {
    const { selectedNotificationTypes, selectedUsers } = state;
    const { byEmail, bySMS } = selectedNotificationTypes;
    return _.map(selectedUsers, (user) => ({
      userId: user.id,
      byEmail,
      bySMS,
    }));
  };

  const onUpdateWorkOrderNotifications = async () => {
    const selections = computeSelections();

    if (_.isEmpty(selections)) {
      AppStore.addError('Please select work order notifications');
      return;
    }

    try {
      setState((prevState) => ({ ...prevState, saving: true }));
      AppStore.addLoading('Saving');

      await ShopStore.sendWorkOrderNotificationConfig(
        ShopStore.currentShop.id,
        selections
      );

      AppStore.openModals.set(EDIT_WORK_ORDER_NOTIFICATION_MODAL_ID, false);
      AppStore.openModals.set(
        EDIT_WORK_ORDER_NOTIFICATION_SUCCESS_MODAL_ID,
        true
      );
      await ShopStore.getShopNotifications(ShopStore.currentShop.id, true);

      setSavedState({
        selectedNotificationTypes: { ...state.selectedNotificationTypes },
        selectedUsers: [...state.selectedUsers],
        saving: false,
      });
    } catch (err) {
      AppStore.addError(
        `Error in updating work order notification config: ${err.message}`
      );
    } finally {
      setState((prevState) => ({ ...prevState, saving: false }));
      AppStore.removeLoading('Saving');
    }
  };

  const onConfirmSuccessModal = async () => {
    await ShopStore.getShopNotifications(ShopStore.currentShop.id, true);
  };

  useEffect(() => {
    if (AppStore.openModals.get(modalId)) {
      setState({ ...savedState, saving: false });
    }
  }, [AppStore.openModals.get(modalId)]);

  return (
    <>
      <SuccessModal
        modalId={EDIT_WORK_ORDER_NOTIFICATION_SUCCESS_MODAL_ID}
        onConfirm={onConfirmSuccessModal}
      >
        <Row gutter={[16, 16]} align="middle" style={{ textAlign: 'center' }}>
          <Col span={24}>
            <h5>Work order notification configs are saved.</h5>
          </Col>
        </Row>
      </SuccessModal>

      <StyledModal
        id={modalId}
        title={title}
        width={850}
        okText="Confirm"
        footer={
          <>
            <Button
              key="cancel"
              onClick={() => {
                setState({ ...savedState, saving: false });
                if (
                  userTableRef.current &&
                  typeof userTableRef.current.clearFiltersAndCollapse ===
                    'function'
                ) {
                  userTableRef.current.clearFiltersAndCollapse();
                }
                AppStore.openModals.set(
                  EDIT_WORK_ORDER_NOTIFICATION_MODAL_ID,
                  false
                );
              }}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={onUpdateWorkOrderNotifications}
              loading={state.saving}
            >
              Confirm
            </Button>
          </>
        }
      >
        <p>
          An alert will be sent automatically whenever a user is assigned to a
          work order or the status changes on a work order.
        </p>

        <Row>
          <Col span={12}>
            <p style={{ fontWeight: 700 }}>Select how to be notified:</p>

            <div>
              <Checkbox
                checked={state.selectedNotificationTypes.byEmail}
                onChange={(e) => onChange('byEmail', e.target.checked)}
              >
                Email
              </Checkbox>

              <Checkbox
                checked={state.selectedNotificationTypes.bySMS}
                onChange={(e) => onChange('bySMS', e.target.checked)}
              >
                Text Message
              </Checkbox>
            </div>
          </Col>
        </Row>

        <UsersTable
          type="WOnotifications_modal"
          ref={userTableRef}
          tableStore={tableStore}
          isUserSelectionEnabled={true}
          sendEmailConfiguration={() => {}}
          sendPriorityAlertsEmailConfiguration={() => {}}
          getCurrentSelectedPriorities={() => {}}
          isDateSelectionEnabled={false}
          isPrioritySelectionEnabled={false}
          enableUserSelection={() => {}}
          isVehicleSelectionEnabled={false}
          sendFleetSegmentConfiguration={() => {}}
          columnKeys={[
            'firstName',
            'lastName',
            'email',
            'phone',
            'workOrderNotifications',
          ]}
          isEditable={false}
          showSetupButton={false}
          onChangeUserIdsSelected={onChangeUsersSelected}
          selectedUserIds={state.selectedUsers.map((u) => u.id)}
        />
      </StyledModal>
    </>
  );
};

WorkOrderModal.propTypes = {
  selectedEmails: PropTypes.array,
  title: PropTypes.string,
  modalId: PropTypes.string,
};

export default observer(WorkOrderModal);
